import React, { useEffect, useState } from "react";
import {
    AlertCircle,
    CheckCircle2,
    Clock3,
    LifeBuoy,
    Loader2,
    Plus,
    Ticket,
    RefreshCw
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getSupportDashboard } from "../services/supportService";
import "./SupportDashboardPage.css";
import PageShell from "../components/layout/PageShell";

export default function SupportDashboardPage() {
    const navigate = useNavigate();

    const [dashboard, setDashboard] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadDashboard();
    }, []);

    const loadDashboard = async () => {
        setLoading(true);

        try {
            const result = await getSupportDashboard();
            setDashboard(result);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <PageShell title="Support Dashboard">
                <div className="support-dashboard-loading">
                    <Loader2 className="spin" size={28} />
                    Loading support dashboard...
                </div>
            </PageShell>
        );
    }

    const recentTickets = dashboard?.recentTickets || [];

    return (
        <PageShell title="Support Dashboard">
            <div className="support-dashboard-page">
                <div className="support-dashboard-hero">
                    <div>
                        <span className="support-dashboard-kicker">Support Center</span>
                        <h1>Support Dashboard</h1>
                        <p>Track support requests, website issues, deployment problems and billing questions.</p>
                    </div>

                    <div className="support-dashboard-actions">
                        <button className="support-secondary-btn" onClick={loadDashboard}>
                            <RefreshCw size={16} />
                            Refresh
                        </button>

                        <button className="support-primary-btn" onClick={() => navigate("/support?new=1")}>
                            <Plus size={16} />
                            Create Ticket
                        </button>
                    </div>
                </div>

                <div className="support-dashboard-stats">
                    <div className="support-stat-card">
                        <LifeBuoy size={22} />
                        <span>Open</span>
                        <strong>{dashboard?.open ?? 0}</strong>
                    </div>

                    <div className="support-stat-card">
                        <Clock3 size={22} />
                        <span>In Progress</span>
                        <strong>{dashboard?.inProgress ?? 0}</strong>
                    </div>

                    <div className="support-stat-card">
                        <AlertCircle size={22} />
                        <span>Waiting</span>
                        <strong>{dashboard?.waiting ?? 0}</strong>
                    </div>

                    <div className="support-stat-card">
                        <CheckCircle2 size={22} />
                        <span>Resolved</span>
                        <strong>{dashboard?.resolved ?? 0}</strong>
                    </div>

                    <div className="support-stat-card">
                        <Ticket size={22} />
                        <span>Total</span>
                        <strong>{dashboard?.total ?? 0}</strong>
                    </div>
                </div>

                <div className="support-dashboard-stats small">
                    <div className="support-stat-card">
                        <span>Tickets Today</span>
                        <strong>{dashboard?.ticketsToday ?? 0}</strong>
                    </div>

                    <div className="support-stat-card">
                        <span>Last 7 Days</span>
                        <strong>{dashboard?.ticketsLast7Days ?? 0}</strong>
                    </div>
                </div>

                <div className="support-dashboard-grid">
                    <div className="support-dashboard-panel">
                        <div className="panel-header">
                            <h2>By Category</h2>
                        </div>

                        {(dashboard?.byCategory || []).length === 0 ? (
                            <div className="support-empty">No category data.</div>
                        ) : (
                            <div className="support-metric-list">
                                {dashboard.byCategory.map((item) => (
                                    <div className="support-metric-row" key={item.key}>
                                        <span>{item.key}</span>
                                        <strong>{item.count}</strong>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="support-dashboard-panel">
                        <div className="panel-header">
                            <h2>By Priority</h2>
                        </div>

                        {(dashboard?.byPriority || []).length === 0 ? (
                            <div className="support-empty">No priority data.</div>
                        ) : (
                            <div className="support-metric-list">
                                {dashboard.byPriority.map((item) => (
                                    <div className="support-metric-row" key={item.key}>
                                        <span>{item.key}</span>
                                        <strong>{item.count}</strong>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="support-dashboard-panel">
                    <div className="panel-header">
                        <h2>Recent Tickets</h2>
                        <button onClick={() => navigate("/support?new=1")}>View All</button>
                    </div>

                    {recentTickets.length === 0 ? (
                        <div className="support-empty">No support tickets yet.</div>
                    ) : (
                        <div className="support-ticket-list">
                            {recentTickets.map((ticket) => (
                                <div
                                    key={ticket.id}
                                    className="support-ticket-row"
                                    onClick={() => navigate(`/support/tickets/${ticket.id}`)}
                                >
                                    <div>
                                        <div className="ticket-number">{ticket.ticketNumber}</div>
                                        <div className="ticket-subject">{ticket.subject}</div>
                                    </div>

                                    <div className={`ticket-status ${ticket.status}`}>
                                        {ticket.status}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </PageShell>
    );
}